/**
 * Curated Defaults Coordinator (C2 — configurator dual-mode refactor)
 * ============================================================================
 * Seeds a fully purchasable "curated default" door at t=0 so the shopper lands
 * on a complete, add-to-cart-ready configuration (100% progress, live ATC)
 * before touching anything — the market-standard "Most Popular" starting point.
 *
 * Fires ONLY when the configurator experience is a dual/curated mode
 * (data-configurator-experience = 'dual_curated_default' | 'dual_full_default').
 * Classic and the legacy simplified 3Q flow are untouched.
 *
 * Flow:
 *   SYSTEM_STATE_FROM_PPD_READY (self-init at module load, golden-standard
 *   bootstrap event — NOT SYSTEM_MAPS_INITIALIZED, which fires before Phase-2
 *   modules load; see url-param-parser)
 *     → build ONE ordered defaults payload (Wood & Finish first so the
 *       material-aware entries resolve against the assumed default wood)
 *     → conductor.claimCelebrationSlot()  (suppress load-time confetti/auto-nav)
 *     → publish BUSINESS_CASCADE_APPLY_CURATED_DEFAULTS (context 'curated_defaults')
 *   cascade-auto-selection-coordinator applies them via the one-step
 *   APPLY_SELECTION primitive and emits BUSINESS_CURATED_DEFAULTS_APPLIED, which
 *   the URL restorer waits for before merging any ?config= selections OVER the
 *   defaults (partial shared links land 100% purchasable).
 *
 * Defaults source: ppd.simplifiedDefaultsByCategory (from the
 * configurator_tab_categories.simplified_default_variant_id metaobject field),
 * shared with the simplified coordinator. Material-aware entries carry
 * { material_aware, by_material_title, fallback }.
 *
 * CCP: this is a Phase-2 bl- module — it never queries the DOM. The experience
 * flag is read through the conductor delegate (_getConfiguratorExperience) and
 * all data via conductor.getPerfectProductDictionary().
 *
 * DATA PREREQUISITE: the 3 curated categories (Wood & Finish, Width, Door
 * Handing) must have simplified_default_variant_id set (best-sellers) for
 * t=0 = 100%. This module dev-logs any curated category still missing a default.
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS && CONSTANTS.EVENTS;

  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  let isInitialized = false;
  let curatedDefaultsTriggered = false;

  // The 3 curated categories that MUST carry a default for a purchasable t=0.
  const CURATED_REQUIRED_CATEGORIES = ['Wood & Finish', 'Width', 'Door Handing'];
  const WOOD_CATEGORY = 'Wood & Finish';

  // ============================================================================
  // FLAG
  // Reads the experience via the conductor delegate (CCP: bl- modules must not
  // query the DOM). Falls back to 'classic' when unavailable.
  // ============================================================================
  function isCuratedEnabled(conductor) {
    try {
      const exp =
        conductor && typeof conductor._getConfiguratorExperience === 'function'
          ? conductor._getConfiguratorExperience()
          : 'classic';
      return exp === 'dual_curated_default' || exp === 'dual_full_default';
    } catch (error) {
      return false;
    }
  }

  // apo_style is NOT a curated experience and must never become one: it has no
  // t=0 seeding here (its Width/Height come from the slider's own "Use default"
  // click in md-config-ui-horizontal-slider.js, deliberately NOT from this
  // coordinator), and isCuratedEnabled() gates initialize()/fireCuratedDefaults.
  //
  // What it DOES get is the reconcile half, and only after a wood is applied:
  // the four categories in APO_STYLE_MATERIAL_SEED_CATEGORIES are filled with
  // their material-aware "Standard" default. Kept as a separate predicate
  // rather than widening isCuratedEnabled() precisely so the t=0 path stays
  // shut for apo_style.
  function isApoMaterialSeed(conductor) {
    try {
      return (
        conductor &&
        typeof conductor._getConfiguratorExperience === 'function' &&
        conductor._getConfiguratorExperience() === 'apo_style'
      );
    } catch (error) {
      return false;
    }
  }

  // The apo_style seed list, read from the shared constant. Empty array (not
  // null) when unavailable, so a missing constant seeds NOTHING rather than
  // silently falling through to "every category with a default".
  function apoSeedCategories() {
    return (CONSTANTS && CONSTANTS.APO_STYLE_MATERIAL_SEED_CATEGORIES) || [];
  }

  // ============================================================================
  // MATERIAL-AWARE RESOLUTION (assumed-wood variant)
  // Adapted from simplified-mode-coordinator.resolveMaterialAwareDefault, but
  // resolves against an ASSUMED wood variant (the curated wood default) rather
  // than the currently-applied Wood & Finish — nothing is applied yet at t=0.
  // ============================================================================
  function resolveMaterialAwareDefault(
    defaultConfig,
    ppd,
    assumedWoodVariantId
  ) {
    if (!assumedWoodVariantId || !ppd || !ppd.variantMap) {
      return (defaultConfig && defaultConfig.fallback) || null;
    }
    try {
      const wfVariant = ppd.variantMap.get(Number(assumedWoodVariantId));
      if (!wfVariant) return defaultConfig.fallback || null;
      const selectedWoodSku =
        wfVariant.woods && wfVariant.woods[0] && wfVariant.woods[0].sku;
      const selectedFinishSku =
        wfVariant.finishes &&
        wfVariant.finishes[0] &&
        wfVariant.finishes[0].sku;
      if (!selectedWoodSku || !selectedFinishSku) {
        return defaultConfig.fallback || null;
      }

      // productId → [{id, v}] index (one pass) to avoid an O(S×V) scan.
      const variantsByProduct = new Map();
      ppd.variantMap.forEach(function (v, id) {
        const pid = v.productId;
        if (!variantsByProduct.has(pid)) variantsByProduct.set(pid, []);
        variantsByProduct.get(pid).push({ id: id, v: v });
      });

      const pointers = defaultConfig.by_material_title || {};
      for (const key of Object.keys(pointers)) {
        const stylePointerId = pointers[key];
        if (!stylePointerId) continue;
        const styleVariant = ppd.variantMap.get(Number(stylePointerId));
        if (!styleVariant) continue;
        const candidates = variantsByProduct.get(styleVariant.productId) || [];
        for (let ci = 0; ci < candidates.length; ci++) {
          const cv = candidates[ci].v;
          if (
            cv.woods &&
            cv.woods[0] &&
            cv.woods[0].sku === selectedWoodSku &&
            cv.finishes &&
            cv.finishes[0] &&
            cv.finishes[0].sku === selectedFinishSku
          ) {
            return String(candidates[ci].id);
          }
        }
      }
    } catch (err) {
      isDev() &&
        console.error(
          '[CuratedDefaultsCoordinator] resolveMaterialAwareDefault error:',
          err
        );
    }
    return defaultConfig.fallback || null;
  }

  // Resolve one category's default variant id — static verbatim, or
  // material-aware against the assumed wood. Returns null when unresolvable.
  function resolveDefaultEntry(categoryId, defaultConfig, ppd, assumedWoodVariantId) {
    if (defaultConfig === null || defaultConfig === undefined) return null;
    let variantId;
    if (typeof defaultConfig === 'object' && defaultConfig.material_aware) {
      variantId = resolveMaterialAwareDefault(
        defaultConfig,
        ppd,
        assumedWoodVariantId
      );
    } else {
      variantId =
        typeof defaultConfig === 'string'
          ? defaultConfig
          : String(defaultConfig);
    }
    return variantId ? { categoryId: categoryId, variantId: variantId } : null;
  }

  // Resolve the wood default (used as the assumed wood for material-aware
  // resolution). Handles static + material-aware-with-fallback shapes.
  function resolveWoodDefault(ppd) {
    const woodConfig = ppd.simplifiedDefaultsByCategory.get(WOOD_CATEGORY);
    if (!woodConfig) return null;
    return typeof woodConfig === 'object'
      ? resolveMaterialAwareDefault(woodConfig, ppd, null) || woodConfig.fallback
      : String(woodConfig);
  }

  // ============================================================================
  // ORDERED PAYLOAD
  // Wood & Finish first (so material-aware entries resolve against it), then
  // the remaining curated-required categories, then everything else.
  // ============================================================================
  function buildOrderedCuratedPayload(conductor) {
    const ppd =
      conductor && typeof conductor.getPerfectProductDictionary === 'function'
        ? conductor.getPerfectProductDictionary()
        : null;
    if (!ppd || !ppd.simplifiedDefaultsByCategory) {
      isDev() &&
        console.warn(
          '[CuratedDefaultsCoordinator] No simplifiedDefaultsByCategory in PPD — no curated defaults to apply'
        );
      return [];
    }

    const defaultsMap = ppd.simplifiedDefaultsByCategory;

    // Resolve the wood default first — it becomes the assumed wood for every
    // material-aware entry.
    const assumedWoodVariantId = resolveWoodDefault(ppd);

    const ordered = [];
    const seen = new Set();

    // 1) Wood & Finish, then the other curated-required categories, first.
    CURATED_REQUIRED_CATEGORIES.forEach(function (categoryId) {
      if (!defaultsMap.has(categoryId)) return;
      const entry = resolveDefaultEntry(
        categoryId,
        defaultsMap.get(categoryId),
        ppd,
        assumedWoodVariantId
      );
      if (entry) {
        ordered.push(entry);
        seen.add(categoryId);
      }
    });

    // 2) Everything else.
    defaultsMap.forEach(function (defaultConfig, categoryId) {
      if (seen.has(categoryId)) return;
      const entry = resolveDefaultEntry(
        categoryId,
        defaultConfig,
        ppd,
        assumedWoodVariantId
      );
      if (entry) {
        ordered.push(entry);
        seen.add(categoryId);
      }
    });

    // Dev-log curated-required categories that still lack a default — these
    // block a purchasable t=0 and must be set on the metaobjects.
    if (isDev()) {
      CURATED_REQUIRED_CATEGORIES.forEach(function (categoryId) {
        if (!seen.has(categoryId)) {
          console.warn(
            '[CuratedDefaultsCoordinator] MISSING default for curated-required category "' +
              categoryId +
              '" — set simplified_default_variant_id on its configurator_tab_categories metaobject for t=0 = 100%.'
          );
        }
      });
    }

    return ordered;
  }

  // ============================================================================
  // FIRE
  // ============================================================================
  function fireCuratedDefaults(conductor) {
    if (curatedDefaultsTriggered) return;
    curatedDefaultsTriggered = true;

    let selections = [];
    try {
      selections = buildOrderedCuratedPayload(conductor);

      // Pre-claim the celebration slot so applying ~20 defaults at load does NOT
      // trigger confetti or a forced Summary auto-nav (only the user reaching
      // the curated completion themselves should celebrate — handled in D1).
      if (conductor && typeof conductor.claimCelebrationSlot === 'function') {
        conductor.claimCelebrationSlot();
      } else {
        isDev() &&
          console.warn(
            '[CuratedDefaultsCoordinator] conductor.claimCelebrationSlot unavailable — a confetti blast may fire at load.'
          );
      }

      isDev() &&
        console.log(
          '[CuratedDefaultsCoordinator] Firing ' +
            selections.length +
            ' curated defaults at t=0'
        );
    } catch (error) {
      // Never leave the URL restorer hanging: fall through to publish (with
      // whatever we resolved, possibly []) so the cascade coordinator still
      // emits BUSINESS_CURATED_DEFAULTS_APPLIED.
      isDev() &&
        console.error(
          '[CuratedDefaultsCoordinator] buildOrderedCuratedPayload failed:',
          error
        );
    }

    // Even a zero-length / failed payload must reach the cascade coordinator so
    // it emits BUSINESS_CURATED_DEFAULTS_APPLIED and the URL restorer proceeds.
    CoreEventBus.publish(CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_CURATED_DEFAULTS, {
      selections: selections,
      context: 'curated_defaults',
      timestamp: Date.now(),
      source: 'CuratedDefaultsCoordinator',
    });
  }

  // ============================================================================
  // C3: CASCADE RE-DEFAULT
  // When a wood/finish change fires the material-authority cascade (which wipes
  // every applied category that was locked to the removed wood), re-resolve
  // those now-empty curated categories against the NEW wood and re-apply them so
  // the door re-skins and progress stays at 100%. Runs only in a curated
  // experience, debounced 50ms so the synchronous cascade has fully settled.
  // ============================================================================
  const RECONCILE_DEBOUNCE_MS = 50;
  let reconcileTimer = null;
  let reconcileInProgress = false;

  // Build the re-default payload: the new/assumed wood (re-defaulted if it was
  // cleared entirely) plus every curated-default category that is now empty
  // (wiped by the cascade), re-resolved against that wood.
  function buildRedefaultPayload(conductor) {
    const ppd =
      conductor && typeof conductor.getPerfectProductDictionary === 'function'
        ? conductor.getPerfectProductDictionary()
        : null;
    if (!ppd || !ppd.simplifiedDefaultsByCategory) return [];

    const sm =
      typeof conductor.getStateMaps === 'function'
        ? conductor.getStateMaps()
        : null;
    const applied = sm && sm.appliedSelections;
    if (!applied) return [];

    const defaultsMap = ppd.simplifiedDefaultsByCategory;
    const selections = [];
    const apoMode = isApoMaterialSeed(conductor);

    // Determine the assumed wood: the currently-applied Wood & Finish, or —
    // if the cascade cleared it entirely — the wood default (re-applied).
    const woodEntry = applied.get(WOOD_CATEGORY);
    let assumedWoodVariantId =
      woodEntry && woodEntry.variantId ? String(woodEntry.variantId) : null;
    if (!assumedWoodVariantId) {
      // 🚨 apo_style NEVER re-applies a wood here. In the curated modes a wood
      // always exists (t=0 seeded it), so restoring one the cascade wiped is a
      // repair. In apo_style the customer picks the wood themselves and an
      // empty Wood & Finish is a legitimate state, so putting the default wood
      // back would be this coordinator making the single most consequential
      // choice on the door on the customer's behalf. Seeding here is supposed
      // to be a CONSEQUENCE of their wood choice, never a substitute for it.
      if (apoMode) return [];
      const woodDefault = resolveWoodDefault(ppd);
      if (woodDefault) {
        assumedWoodVariantId = woodDefault;
        selections.push({ categoryId: WOOD_CATEGORY, variantId: woodDefault });
      }
    }
    if (!assumedWoodVariantId) return []; // cannot re-skin without a wood

    // Re-default every OTHER eligible category that is now empty.
    // Curated modes: everything carrying a default. apo_style: only the four
    // in APO_STYLE_MATERIAL_SEED_CATEGORIES -- this narrowing is load-bearing,
    // since without it a wood click would seed the ENTIRE door (cabinet doors,
    // knobs, locks, delivery), which is emphatically not what apo_style is for.
    const apoAllowed = apoMode ? apoSeedCategories() : null;
    // An ENFORCED_PAIR clear leaves a category deliberately empty until the
    // customer chooses. Reconcile runs on every material recompute, so without
    // this it would re-offer that default each time (the cascade coordinator
    // refuses it too; this keeps the payload and REDEFAULT_COMPLETED honest).
    const isHeld =
      typeof conductor.isEnforcedPairHeld === 'function'
        ? function (categoryId) {
            return conductor.isEnforcedPairHeld(categoryId) === true;
          }
        : function () {
            return false;
          };
    defaultsMap.forEach(function (defaultConfig, categoryId) {
      if (categoryId === WOOD_CATEGORY) return;
      if (apoAllowed && apoAllowed.indexOf(categoryId) === -1) return;
      if (applied.has(categoryId)) return; // survived the cascade → keep it
      if (isHeld(categoryId)) return; // held empty by an enforced pair
      const entry = resolveDefaultEntry(
        categoryId,
        defaultConfig,
        ppd,
        assumedWoodVariantId
      );
      if (entry) selections.push(entry);
    });

    return selections;
  }

  function runReconcile() {
    reconcileTimer = null;
    const conductor =
      window.MurphyDoorConfigurator.conductor ||
      window.MurphyDoorConfigurator.main;
    if (!conductor) return;
    // Curated modes reconcile after the material cascade; apo_style reconciles
    // after a wood is applied. Same builder, different entry conditions.
    if (!isCuratedEnabled(conductor) && !isApoMaterialSeed(conductor)) return;
    if (reconcileInProgress) return; // loop guard
    try {
      const selections = buildRedefaultPayload(conductor);
      if (!selections.length) return;
      reconcileInProgress = true;
      isDev() &&
        console.log(
          '[CuratedDefaultsCoordinator] Re-defaulting ' +
            selections.length +
            ' categories after material change'
        );
      // Reuse the bulk-apply path; context 'cascade_redefault' stamps the
      // provenance so these read as "Included" (not user-chosen). This does NOT
      // emit BUSINESS_CURATED_DEFAULTS_APPLIED (that is initial-load only).
      CoreEventBus.publish(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_CURATED_DEFAULTS,
        {
          selections: selections,
          context: 'cascade_redefault',
          timestamp: Date.now(),
          source: 'CuratedDefaultsCoordinator.reconcile',
        }
      );
      CoreEventBus.publish(
        CONSTANTS.EVENTS.BUSINESS_CURATED_REDEFAULT_COMPLETED,
        {
          count: selections.length,
          timestamp: Date.now(),
          source: 'CuratedDefaultsCoordinator.reconcile',
        }
      );
    } catch (error) {
      isDev() &&
        console.error('[CuratedDefaultsCoordinator] reconcile failed:', error);
    } finally {
      reconcileInProgress = false;
    }
  }

  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(runReconcile, RECONCILE_DEBOUNCE_MS);
  }

  // ============================================================================
  // INIT (self-init on the golden-standard bootstrap event)
  // ============================================================================
  function initialize(conductor) {
    if (isInitialized) return false;
    if (!conductor) return false;
    if (!isCuratedEnabled(conductor)) {
      isDev() &&
        console.log(
          '[CuratedDefaultsCoordinator] Curated experience not enabled — standing down'
        );
      return false;
    }
    isInitialized = true;
    fireCuratedDefaults(conductor);
    return true;
  }

  if (CoreEventBus && EVENTS) {
    CoreEventBus.subscribe(CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY, function () {
      const conductor =
        window.MurphyDoorConfigurator.conductor ||
        window.MurphyDoorConfigurator.main;
      if (!conductor) {
        isDev() &&
          console.error(
            '[CuratedDefaultsCoordinator] Conductor unavailable on SYSTEM_STATE_FROM_PPD_READY'
          );
        return;
      }
      initialize(conductor);
    });

    // C3: reconcile (re-default) after the material-authority cascade wipes
    // material-locked categories. Both events fire during that cascade; the
    // 50ms debounce coalesces them and waits for the synchronous wipe to finish.
    CoreEventBus.subscribe(
      CONSTANTS.EVENTS.MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED,
      scheduleReconcile
    );
    CoreEventBus.subscribe(
      CONSTANTS.EVENTS.MATERIAL_PREFERENCE_CLEARED,
      scheduleReconcile
    );

    // apo_style: seed the casing/entablature defaults once a wood is applied.
    //
    // 🚨 THE EVENT CHOICE IS MEASURED, NOT ASSUMED. The two subscriptions
    // above do NOT fire on this path -- instrumented live on the preview theme
    // 2026-08-27, a wood apply in apo_style emits exactly:
    //   MATERIAL_PREFERENCE_APPLY_STARTED -> _VALIDATION -> _PROGRESS ->
    //   MATERIAL_FILTER_STATE_UPDATED -> MATERIAL_PREFERENCE_APPLIED ->
    //   MATERIAL_PREFERENCE_APPLY_COMPLETED
    // and MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED never appears. Hooking
    // the existing pair would have looked correct in review and done nothing.
    //
    // _APPLY_COMPLETED is the terminal event, so the wood is in
    // appliedSelections and the material filters have settled by the time the
    // 50ms debounce fires. Gated to apo_style inside the handler so the curated
    // modes keep their existing trigger set unchanged.
    CoreEventBus.subscribe(
      CONSTANTS.EVENTS.MATERIAL_PREFERENCE_APPLY_COMPLETED,
      function () {
        const conductor =
          window.MurphyDoorConfigurator.conductor ||
          window.MurphyDoorConfigurator.main;
        if (!isApoMaterialSeed(conductor)) return;
        scheduleReconcile();
      }
    );
  }

  // Public API (single-namespace publication + testing hooks).
  window.MurphyDoorConfigurator.curatedDefaultsCoordinator = {
    initialize: initialize,
    isCuratedEnabled: isCuratedEnabled,
    // Exposed for unit testing.
    _buildOrderedCuratedPayload: buildOrderedCuratedPayload,
    _buildRedefaultPayload: buildRedefaultPayload,
    _resolveMaterialAwareDefault: resolveMaterialAwareDefault,
  };
})(window);
